import React, { useCallback, useState } from "react";
import useAdminPreview from "utilities/hooks/use-admin-preview";
import ArticleTableOfContentsRecord from "models/view-models/table-of-contents/article-table-of-contents-record";
import { useLocation, useParams } from "react-router-dom";
import { ArticleParams } from "interfaces/routing/route-params";
import StringUtils from "utilities/string-utils";
import CollapsePanel from "molecules/collapse-panel/collapse-panel";
import TableOfContentsCollapsePanelHeader from "./table-of-contents-collapse-panel-header";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import { IconSizes } from "atoms/constants/icon-sizes";
import useBookviewTableOfContents from "utilities/hooks/domain/publications/use-bookview-table-of-contents";
import Loader from "atoms/loaders/loader";
import { LoaderStyles } from "atoms/constants/loader-styles";
import TableOfContentsSection from "./table-of-contents-section";
import { SectionParentType } from "utilities/enumerations/section-parent-type";
import { PartRecord } from "internal";
import TableOfContentsCollapsePanelLink from "./table-of-contents-collapse-panel-link";

interface TableOfContentsArticleProps {
    articles: ArticleTableOfContentsRecord[];
    chapterNfpaLabel: string;
    isPublic?: boolean;
    locale?: string;
    activeSectionId?: number | null;
    publicationId?: number;
    useArticleList?: boolean;
}

const baseClassName = "c-table-of-contents-section";

const TableOfContentsArticle: React.FunctionComponent<
    TableOfContentsArticleProps
> = (props: TableOfContentsArticleProps) => {
    const {
        articles,
        chapterNfpaLabel,
        isPublic = false,
        locale,
        publicationId,
        useArticleList = false,
    } = props;
    const { isAdminPreview } = useAdminPreview();
    const { inMobileView } = useBreakpointRanges();

    // This prevents scrolling back to the active section when the user expands a new article
    const [autoScrollToSection, setAutoScrollToSection] = useState(true);

    const params = useParams<ArticleParams>();
    const { pathname } = useLocation();

    const sortedArticles = articles.sort(
        (a, b) => (a.displaySequence ?? 0) - (b.displaySequence ?? 0)
    );

    const getIsActive = (article: ArticleTableOfContentsRecord) => {
        return (
            pathname.includes(`articles/${article.nfpaLabel}`) ||
            pathname.includes(`articles/${article.id}`)
        );
    };

    const { articlesLoading, getTableOfContentsArticleSection } =
        useBookviewTableOfContents();

    const fetchArticleSections = useCallback(
        (article: ArticleTableOfContentsRecord) => {
            const articleHasSections =
                article.sections && article.sections.length > 0;

            if (!articleHasSections && publicationId != null) {
                getTableOfContentsArticleSection(
                    publicationId,
                    article.id,
                    article.chapterId
                );
            }

            setAutoScrollToSection(false);
        },
        [getTableOfContentsArticleSection, publicationId]
    );

    return (
        <nav className={baseClassName}>
            <ul className="-nav-list">
                {!useArticleList &&
                    sortedArticles.map((article) => {
                        const isActive = getIsActive(article);
                        const className = StringUtils.joinClassNames([
                            "c-table-of-contents-chapter",
                            "-sub-header",
                            "-sub-panel",
                            isActive ? "-active" : "",
                        ]);

                        return (
                            <div className={className} key={article.id}>
                                <CollapsePanel
                                    buttonAriaText={article.getDisplayTitle(
                                        locale
                                    )}
                                    collapse={!isActive}
                                    modifierCSSClassName="-panel -sub-panel -sub-panel-expanded"
                                    iconSize={
                                        inMobileView
                                            ? IconSizes.Base
                                            : undefined
                                    }
                                    onOpen={() => fetchArticleSections(article)}
                                    panelTop={
                                        <TableOfContentsCollapsePanelHeader
                                            displayTitle={article.getDisplayTitle(
                                                locale
                                            )}
                                            to={article.getRoute(
                                                params,
                                                chapterNfpaLabel,
                                                isAdminPreview
                                            )}
                                            isPublic={isPublic}
                                        />
                                    }>
                                    {articlesLoading ? (
                                        <Loader
                                            type={LoaderStyles.LinkGlyphBlue}
                                        />
                                    ) : (
                                        <ul className="-nav-list">
                                            <TableOfContentsSection
                                                isPublic={isPublic}
                                                chapterNfpaLabel={
                                                    chapterNfpaLabel
                                                }
                                                sections={
                                                    article.sections ?? []
                                                }
                                                useHashLinks={false}
                                                parentType={
                                                    SectionParentType.Article
                                                }
                                                articleNfpaLabel={
                                                    article.nfpaLabel
                                                }
                                                parts={
                                                    article.parts?.map(
                                                        (p) => new PartRecord(p)
                                                    ) ?? []
                                                }
                                                autoScroll={autoScrollToSection}
                                            />
                                        </ul>
                                    )}
                                </CollapsePanel>
                            </div>
                        );
                    })}

                {useArticleList &&
                    sortedArticles.map((article) => {
                        return (
                            <TableOfContentsCollapsePanelLink
                                key={article.id}
                                dataTestDisplaySequence={
                                    article.displaySequence
                                }
                                dataTestId={"article-link"}
                                hasBookmark={article.hasBookmark}
                                index={article.id}
                                isPublic={isPublic}
                                to={article.getRoute(
                                    params,
                                    chapterNfpaLabel,
                                    isAdminPreview
                                )}>
                                <span>{article.getDisplayTitle(locale)}</span>
                            </TableOfContentsCollapsePanelLink>
                        );
                    })}
            </ul>
        </nav>
    );
};

export default React.memo(TableOfContentsArticle);
